/**
 * Goods Receipting attach parity helpers.
 *
 * The GR attach path already knows the chosen order, so we pre-fill trusted
 * order context (PO, venue supplier, linked order) and only let OCR fill
 * document fields (number / date / total). OCR must never silently overwrite
 * the order's PO or supplier — mismatches are soft-flagged for the user.
 */

import {
  applyOcrSuggestionsToUploadFile,
  applyInvoiceTotalSignForDocumentType,
  formatInvoiceTotalDisplay,
  formatPenniesAsPoundsInput,
  matchVenueSupplierFromOcrName,
  normaliseInvoiceDateInput,
  parseInvoiceTotalToPennies,
} from './invoiceUploadHelpers'
import {
  formatTolerancePounds,
  INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES,
} from './invoiceMatchToleranceHelpers'
import { getTotalSpendOrderedWithVAT } from './priceFunctions'

export const GR_OCR_MISMATCH = {
  PO: 'po',
  SUPPLIER: 'supplier',
}

function idString(value) {
  if (value == null) return ''
  if (typeof value === 'object' && value._id != null) return String(value._id)
  return String(value)
}

/** Light PO compare for mismatch flags (case / whitespace insensitive). */
export function normalisePoForCompare(value) {
  return String(value || '')
    .toUpperCase()
    .replace(/\s+/g, '')
}

export function poValuesMatch(a, b) {
  const left = normalisePoForCompare(a)
  const right = normalisePoForCompare(b)
  if (!left || !right) return false
  return left === right
}

/**
 * Prefill an upload-file draft from the chosen GR order.
 * Never copies the order total into the invoice total.
 */
export function prefillUploadFileFromOrder(file = {}, order = {}) {
  const venueSupplierId = idString(order?.venueSupplier?._id)
  const linkedOrderId =
    Array.isArray(order?.orderIds) && order.orderIds[0]
      ? String(order.orderIds[0])
      : order?._id
      ? String(order._id)
      : null

  return {
    ...file,
    supplier: venueSupplierId || file.supplier || null,
    supplierTitle:
      order?.venueSupplier?.title ||
      order?.supplier?.title ||
      file.supplierTitle ||
      '',
    purchaseOrderNumber:
      String(order?.purchaseOrderNumber || '').trim() ||
      file.purchaseOrderNumber ||
      '',
    linkedOrderId: linkedOrderId || file.linkedOrderId || null,
    // Prefer existing order metadata when present; OCR may still fill blanks.
    invoiceNumber:
      file.invoiceNumber || String(order?.invoiceNumber || '').trim() || '',
    invoiceDate: file.invoiceDate || null,
    // Order context is trusted — mark supplier as user-confirmed for matching.
    supplierConfirmedByUser: Boolean(venueSupplierId),
    grOrderContext: {
      purchaseOrderNumber: String(order?.purchaseOrderNumber || '').trim(),
      venueSupplierId: venueSupplierId || null,
      supplierTitle:
        order?.venueSupplier?.title || order?.supplier?.title || '',
      linkedOrderId: linkedOrderId || null,
    },
    grOcrMismatches: Array.isArray(file.grOcrMismatches)
      ? file.grOcrMismatches
      : [],
  }
}

/**
 * Apply OCR suggestions for GR attach without letting OCR overwrite the
 * trusted order PO / supplier. Soft-flags mismatches instead.
 */
export function applyOcrSuggestionsKeepingOrderContext(
  file = {},
  ocrData = {},
  venueSuppliers = [],
) {
  const orderContext = file.grOrderContext || {}
  const suggested = ocrData?.suggested || null
  const withOcr = applyOcrSuggestionsToUploadFile(file, ocrData, venueSuppliers)

  const ocrPo = withOcr.ocrSuggested?.poNumber || ''
  const ocrMatchedSupplier = suggested
    ? matchVenueSupplierFromOcrName(suggested.supplierName, venueSuppliers)
    : null
  const ocrSupplierId = ocrMatchedSupplier?._id || null
  const ocrSupplierTitle =
    suggested?.supplierName || withOcr.ocrSuggested?.supplierName || ''

  const mismatches = []

  if (
    ocrPo &&
    orderContext.purchaseOrderNumber &&
    !poValuesMatch(ocrPo, orderContext.purchaseOrderNumber)
  ) {
    mismatches.push({
      code: GR_OCR_MISMATCH.PO,
      message: `The invoice shows PO “${ocrPo}”, but this order is ${orderContext.purchaseOrderNumber}. Check you picked the right order.`,
      ocrValue: ocrPo,
      orderValue: orderContext.purchaseOrderNumber,
    })
  }

  const orderSupplierId = orderContext.venueSupplierId
  if (
    orderSupplierId &&
    ocrSupplierId &&
    String(ocrSupplierId) !== String(orderSupplierId)
  ) {
    mismatches.push({
      code: GR_OCR_MISMATCH.SUPPLIER,
      message: `The invoice suggests a different supplier (“${
        ocrSupplierTitle || 'unknown'
      }”) than this order’s supplier. Check you picked the right order.`,
      ocrValue: ocrSupplierTitle,
      orderValue: orderContext.supplierTitle,
    })
  } else if (
    !ocrSupplierId &&
    ocrSupplierTitle &&
    orderContext.supplierTitle &&
    !supplierTitlesLooselyMatch(ocrSupplierTitle, orderContext.supplierTitle)
  ) {
    mismatches.push({
      code: GR_OCR_MISMATCH.SUPPLIER,
      message: `The invoice shows supplier “${ocrSupplierTitle}”, which doesn’t look like “${orderContext.supplierTitle}”. Check you picked the right order.`,
      ocrValue: ocrSupplierTitle,
      orderValue: orderContext.supplierTitle,
    })
  }

  // Keep trusted order context; OCR fills document number / date / total / type.
  return {
    ...withOcr,
    supplier: orderContext.venueSupplierId || withOcr.supplier || null,
    supplierTitle: orderContext.supplierTitle || withOcr.supplierTitle || '',
    purchaseOrderNumber:
      orderContext.purchaseOrderNumber || withOcr.purchaseOrderNumber || '',
    linkedOrderId: orderContext.linkedOrderId || withOcr.linkedOrderId || null,
    invoiceNumber: suggested?.invoiceNumber || withOcr.invoiceNumber || '',
    invoiceDate:
      normaliseInvoiceDateInput(suggested?.invoiceDate) || withOcr.invoiceDate,
    supplierConfirmedByUser: Boolean(orderContext.venueSupplierId),
    grOrderContext: orderContext,
    grOcrMismatches: mismatches,
  }
}

export function supplierTitlesLooselyMatch(left, right) {
  const a = String(left || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  const b = String(right || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
  if (!a || !b) return false
  return a === b || a.includes(b) || b.includes(a)
}

/**
 * Resolve match tolerance for a GR order: supplier override → venue → £1.00.
 */
export function resolveGoodsReceiptingTolerancePennies({
  venue = null,
  venueSupplier = null,
  settings = null,
} = {}) {
  const supplierId = idString(
    venueSupplier?._id || venueSupplier?.venueSupplierId,
  )

  if (settings && supplierId) {
    const override = (settings.supplierOverrides || []).find(
      row => String(row.venueSupplierId) === supplierId,
    )
    if (override?.invoiceMatchTolerance != null) {
      const value = Math.round(Number(override.invoiceMatchTolerance))
      if (Number.isFinite(value) && value >= 0) return value
    }
  }

  if (settings?.invoiceMatchTolerance != null) {
    const value = Math.round(Number(settings.invoiceMatchTolerance))
    if (Number.isFinite(value) && value >= 0) return value
  }

  if (venue?.invoiceMatchTolerance != null) {
    const value = Math.round(Number(venue.invoiceMatchTolerance))
    if (Number.isFinite(value) && value >= 0) return value
  }

  return INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES
}

/**
 * Gross (inc VAT) order total in pennies from receipting product lines.
 * Returns null when lines are missing — never invent from ex-VAT table fields.
 */
export function getOrderGrossTotalPenniesFromLines(orderLines = []) {
  if (!Array.isArray(orderLines) || orderLines.length === 0) {
    return null
  }
  const pounds = getTotalSpendOrderedWithVAT(orderLines)
  if (pounds == null || !Number.isFinite(Number(pounds))) {
    return null
  }
  return Math.round(Number(pounds) * 100)
}

/**
 * Build the order-vs-invoice difference strip for the GR confirm modal.
 */
export function buildGoodsReceiptingDifferenceSummary({
  invoiceTotalInput = '',
  orderGrossPennies = null,
  tolerancePennies = INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES,
  documentType = 'invoice',
} = {}) {
  const invoiceTotalPennies = parseInvoiceTotalToPennies(invoiceTotalInput)
  const tolerance =
    tolerancePennies == null || !Number.isFinite(Number(tolerancePennies))
      ? INVOICE_MATCH_TOLERANCE_DEFAULT_PENNIES
      : Math.max(0, Math.round(Number(tolerancePennies)))

  const isCreditNote = documentType === 'creditNote'
  const differencePennies =
    invoiceTotalPennies == null || orderGrossPennies == null
      ? null
      : invoiceTotalPennies - orderGrossPennies

  const withinTolerance =
    !isCreditNote &&
    differencePennies != null &&
    Math.abs(differencePennies) <= tolerance

  return {
    invoiceTotalPennies,
    orderGrossPennies:
      orderGrossPennies == null ? null : Math.round(Number(orderGrossPennies)),
    differencePennies,
    tolerancePennies: tolerance,
    withinTolerance,
    canOfferReconcile: withinTolerance,
    isCreditNote,
    invoiceTotalDisplay: formatInvoiceTotalDisplay(invoiceTotalPennies),
    orderGrossDisplay: formatInvoiceTotalDisplay(orderGrossPennies),
    differenceDisplay:
      differencePennies == null
        ? '—'
        : formatInvoiceTotalDisplay(differencePennies),
    toleranceDisplay: formatTolerancePounds(tolerance),
  }
}

/**
 * Build attach API body fields for total provenance from a confirm file.
 */
export function buildGoodsReceiptingAttachTotalFields(file = {}) {
  const pennies = parseInvoiceTotalToPennies(file.invoiceTotalInput)
  if (pennies == null) {
    return {
      invoiceTotal: undefined,
      invoiceTotalSource: undefined,
    }
  }
  return {
    invoiceTotal: applyInvoiceTotalSignForDocumentType(
      file.documentType || 'invoice',
      pennies,
    ),
    invoiceTotalSource:
      file.invoiceTotalSource ||
      (file.invoiceTotalInput ? 'manual' : undefined),
  }
}

export function formatOrderGrossAsPoundsInput(orderGrossPennies) {
  return formatPenniesAsPoundsInput(orderGrossPennies)
}
